import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Package } from "lucide-react";
import * as LucideIcons from "lucide-react";

type FavoriteApp = {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  icon_name: string;
};

export function FavoriteApps() {
  const navigate = useNavigate();
  const [apps, setApps] = useState<FavoriteApp[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavoriteApps();
  }, []);

  const loadFavoriteApps = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar favoritos do usuário ordenados por data de criação (mais recentes primeiro)
      const { data: favorites, error: favoritesError } = await supabase
        .from('user_favorite_extensions')
        .select('extension_id, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (favoritesError) {
        console.error('Erro ao carregar favoritos:', favoritesError);
        setLoading(false);
        return;
      }

      if (!favorites || favorites.length === 0) {
        setLoading(false);
        return;
      }

      const extensionIds = favorites.map(f => f.extension_id);

      // Buscar detalhes das extensions favoritadas
      const { data: extensions } = await supabase
        .from('extensions')
        .select('id, title, slug, short_description, icon_name, is_active, is_suspended')
        .in('id', extensionIds)
        .eq('is_active', true)
        .eq('is_suspended', false);

      if (!extensions) {
        setLoading(false);
        return;
      }

      // Manter a ordem dos favoritos (mais recentes primeiro)
      const orderedApps = extensionIds
        .map(id => extensions.find(e => e.id === id))
        .filter(Boolean) as FavoriteApp[];

      setApps(orderedApps);
    } catch (error) {
      console.error('Error loading favorite apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as unknown)[iconName] || Package;
    return Icon;
  };

  // Skeleton loading
  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">Meus Aplicativos</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="p-4 flex flex-col items-center gap-2">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <Skeleton className="h-3 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Não mostrar seção se não houver apps favoritados
  if (apps.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Meus Aplicativos</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {apps.map((app) => {
          const Icon = getIcon(app.icon_name);
          return (
            <Card
              key={app.id}
              onClick={() => navigate(`/app/aplicativos/${app.slug}`)}
              className="cursor-pointer hover:border-primary/30 hover:shadow-md transition-all"
            >
              <CardContent className="p-4 flex flex-col items-center text-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <p className="text-xs font-medium truncate w-full">
                  {app.title}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

